// src/app/AITemplateBuilder.js - AI-Powered Template Builder
import React, { useState } from 'react';
import {
  Sparkles,
  Wand2,
  ArrowLeft,
  Plus,
  Trash2,
  Save,
  Eye,
  Check,
  Loader2,
  Table2,
  Zap,
  Clock,
} from 'lucide-react';

const PLATFORMS = [
  { id: 'google_sheets', name: 'Google Sheets', icon: '📊', color: 'bg-green-100 text-green-700 border-green-300' },
  { id: 'excel', name: 'Excel', icon: '📗', color: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
  { id: 'notion', name: 'Notion', icon: '📝', color: 'bg-gray-100 text-gray-700 border-gray-300' },
  { id: 'airtable', name: 'Airtable', icon: '🗃️', color: 'bg-yellow-100 text-yellow-700 border-yellow-300' },
];

const CATEGORIES = [
  { value: 'business-finance', label: 'Business & Finance' },
  { value: 'project-management', label: 'Project Management' },
  { value: 'marketing-sales', label: 'Marketing & Sales' },
  { value: 'personal-productivity', label: 'Personal Productivity' },
  { value: 'education-learning', label: 'Education & Learning' },
  { value: 'health-fitness', label: 'Health & Fitness' },
  { value: 'real-estate', label: 'Real Estate' },
  { value: 'hr-people', label: 'HR & People' },
  { value: 'inventory-operations', label: 'Inventory & Operations' },
  { value: 'creative-content', label: 'Creative & Content' },
];

const EXAMPLE_PROMPTS = [
  { text: 'Create a monthly budget tracker with income, expenses, and savings goals', icon: '💰' },
  { text: 'Build a project management board with tasks, priorities, deadlines, and team assignments', icon: '📋' },
  { text: 'Design a CRM contact tracker with lead status, follow-up dates, and deal values', icon: '🤝' },
  { text: 'Make a fitness tracker with workout logs, nutrition, weight, and progress charts', icon: '💪' },
];

const PROGRESS_MESSAGES = [
  'Analyzing your requirements...',
  'Designing template structure...',
  'Creating columns and formulas...',
  'Generating preview...',
];

const MOCK_GENERATED_TEMPLATE = {
  name: 'Monthly Budget Tracker',
  description: 'A comprehensive monthly budget tracker with income tracking, expense categories, savings goals, and visual dashboards.',
  columns: [
    { name: 'Date', type: 'Date', sample: '2026-03-01', description: 'Transaction date' },
    { name: 'Category', type: 'Dropdown', sample: 'Housing', description: 'Expense category (Housing, Food, Transport, etc.)' },
    { name: 'Description', type: 'Text', sample: 'Monthly rent', description: 'Transaction description' },
    { name: 'Amount', type: 'Number', sample: '$1,500.00', description: 'Transaction amount' },
    { name: 'Type', type: 'Dropdown', sample: 'Expense', description: 'Income or Expense' },
    { name: 'Payment Method', type: 'Dropdown', sample: 'Bank Transfer', description: 'How payment was made' },
    { name: 'Running Balance', type: 'Formula', sample: '=SUM()', description: 'Auto-calculated running balance' },
    { name: 'Notes', type: 'Text', sample: 'Paid on 1st', description: 'Optional notes' },
  ],
  sheets: ['Transactions', 'Summary Dashboard', 'Categories', 'Settings'],
  tags: ['budget', 'finance', 'tracking', 'monthly', 'expenses'],
  features: [
    'Automatic category totals',
    'Monthly vs. yearly comparison',
    'Visual spending charts',
    'Savings goal tracking',
    'Custom expense categories',
  ],
  useCases: [
    'Personal budget management',
    'Household expense tracking',
    'Small business bookkeeping',
    'Freelancer income/expense tracking',
  ],
};

const MOCK_BUILD_HISTORY = [
  { id: 1, prompt: 'Create a monthly budget tracker...', platform: 'google_sheets', date: '2026-03-18', status: 'completed' },
  { id: 2, prompt: 'Build a project management board...', platform: 'notion', date: '2026-03-17', status: 'completed' },
  { id: 3, prompt: 'Design an inventory tracker...', platform: 'excel', date: '2026-03-15', status: 'completed' },
];

const AITemplateBuilder = ({ setCurrentPage }) => {
  const [step, setStep] = useState(1);
  const [prompt, setPrompt] = useState('');
  const [selectedPlatform, setSelectedPlatform] = useState('google_sheets');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [progressIndex, setProgressIndex] = useState(0);
  const [generatedTemplate, setGeneratedTemplate] = useState(null);
  const [editableColumns, setEditableColumns] = useState([]);
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [pricing, setPricing] = useState('free');
  const [price, setPrice] = useState('');
  const [saving, setSaving] = useState(false);

  const handleGenerate = () => {
    if (!prompt.trim()) return;
    setIsGenerating(true);
    setProgressIndex(0);
    setStep(2);

    // Simulate AI generation with progress messages
    const interval = setInterval(() => {
      setProgressIndex((prev) => {
        if (prev >= PROGRESS_MESSAGES.length - 1) {
          clearInterval(interval);
          setTimeout(() => {
            setIsGenerating(false);
            setGeneratedTemplate(MOCK_GENERATED_TEMPLATE);
            setEditableColumns([...MOCK_GENERATED_TEMPLATE.columns]);
            setTemplateName(MOCK_GENERATED_TEMPLATE.name);
            setTemplateDescription(MOCK_GENERATED_TEMPLATE.description);
            setStep(3);
          }, 800);
          return prev;
        }
        return prev + 1;
      });
    }, 1000);
  };

  const handleAddColumn = () => {
    setEditableColumns((prev) => [
      ...prev,
      { name: '', type: 'Text', sample: '', description: '' },
    ]);
  };

  const handleRemoveColumn = (index) => {
    setEditableColumns((prev) => prev.filter((_, i) => i !== index));
  };

  const handleColumnChange = (index, field, value) => {
    setEditableColumns((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleSave = async (publish = false) => {
    setSaving(true);
    // TODO: Save to Supabase templates table
    try {
      await new Promise((resolve) => setTimeout(resolve, 1500));
      setStep(4);
    } catch (err) {
      console.error('Error saving template:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleUseExample = (exampleText) => {
    setPrompt(exampleText);
  };

  return (
    <div className="p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-2">
            <div className="p-2 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900">AI Template Builder</h1>
          </div>
          <p className="text-gray-500 ml-14">
            Describe what you need and AI will generate a template for you
          </p>
        </div>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between max-w-2xl">
            {['Describe', 'Generate', 'Customize', 'Publish'].map((label, i) => {
              const stepNum = i + 1;
              const isActive = step === stepNum;
              const isCompleted = step > stepNum;
              return (
                <React.Fragment key={label}>
                  <div className="flex items-center">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                        isCompleted
                          ? 'bg-green-500 text-white'
                          : isActive
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 text-gray-500'
                      }`}
                    >
                      {isCompleted ? <Check className="w-4 h-4" /> : stepNum}
                    </div>
                    <span
                      className={`ml-2 text-sm font-medium ${
                        isActive ? 'text-blue-600' : isCompleted ? 'text-green-600' : 'text-gray-400'
                      }`}
                    >
                      {label}
                    </span>
                  </div>
                  {i < 3 && (
                    <div className={`flex-1 h-0.5 mx-4 ${step > stepNum ? 'bg-green-500' : 'bg-gray-200'}`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Step 1: Describe */}
            {step === 1 && (
              <div className="space-y-6">
                <div className="bg-white p-6 rounded-xl shadow border">
                  <h2 className="text-lg font-semibold mb-4">What template do you need?</h2>
                  <textarea
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder="Describe the template you want to create in detail. For example: 'Create a project tracker with task name, assigned person, priority, deadline, status, and progress percentage columns...'"
                    rows={5}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  />

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-3">Platform</label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {PLATFORMS.map((platform) => (
                        <button
                          key={platform.id}
                          onClick={() => setSelectedPlatform(platform.id)}
                          className={`p-3 rounded-lg border-2 text-center transition-all ${
                            selectedPlatform === platform.id
                              ? 'border-blue-500 bg-blue-50 shadow-sm'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <span className="text-2xl block mb-1">{platform.icon}</span>
                          <span className="text-sm font-medium">{platform.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                    <select
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select a category...</option>
                      {CATEGORIES.map((cat) => (
                        <option key={cat.value} value={cat.value}>
                          {cat.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <button
                    onClick={handleGenerate}
                    disabled={!prompt.trim()}
                    className="mt-6 w-full py-3 px-6 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 transition-all"
                  >
                    <Wand2 className="w-5 h-5" />
                    <span>Generate Template</span>
                  </button>
                </div>

                {/* Example Prompts */}
                <div className="bg-white p-6 rounded-xl shadow border">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                    <Zap className="w-4 h-4 mr-2 text-yellow-500" />
                    Try an example
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {EXAMPLE_PROMPTS.map((example, i) => (
                      <button
                        key={i}
                        onClick={() => handleUseExample(example.text)}
                        className="text-left p-3 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all"
                      >
                        <span className="text-lg mr-2">{example.icon}</span>
                        <span className="text-sm text-gray-700">{example.text}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Generating */}
            {step === 2 && isGenerating && (
              <div className="bg-white p-12 rounded-xl shadow border text-center">
                <div className="relative inline-flex items-center justify-center w-20 h-20 mb-6">
                  <div className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-400 to-purple-400 animate-ping opacity-25" />
                  <div className="relative bg-gradient-to-r from-blue-600 to-purple-600 rounded-full p-5">
                    <Sparkles className="w-10 h-10 text-white animate-pulse" />
                  </div>
                </div>
                <h2 className="text-xl font-bold text-gray-900 mb-4">Building Your Template</h2>
                <div className="space-y-3 max-w-md mx-auto">
                  {PROGRESS_MESSAGES.map((msg, i) => (
                    <div
                      key={i}
                      className={`flex items-center space-x-3 transition-all duration-300 ${
                        i <= progressIndex ? 'opacity-100' : 'opacity-30'
                      }`}
                    >
                      {i < progressIndex ? (
                        <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                      ) : i === progressIndex ? (
                        <Loader2 className="w-5 h-5 text-blue-500 animate-spin flex-shrink-0" />
                      ) : (
                        <div className="w-5 h-5 rounded-full border-2 border-gray-300 flex-shrink-0" />
                      )}
                      <span className="text-sm text-gray-700">{msg}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Step 3: Customize */}
            {step === 3 && generatedTemplate && (
              <div className="space-y-6">
                <div className="bg-white p-6 rounded-xl shadow border">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold">Template Structure</h2>
                    <button
                      onClick={() => setStep(1)}
                      className="text-sm text-blue-600 hover:text-blue-700 flex items-center"
                    >
                      <ArrowLeft className="w-4 h-4 mr-1" />
                      Start Over
                    </button>
                  </div>

                  <div className="space-y-4 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Template Name</label>
                      <input
                        type="text"
                        value={templateName}
                        onChange={(e) => setTemplateName(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                      <textarea
                        value={templateDescription}
                        onChange={(e) => setTemplateDescription(e.target.value)}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  {/* Sheet Tabs */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Sheet Tabs</label>
                    <div className="flex flex-wrap gap-2">
                      {generatedTemplate.sheets.map((sheet, i) => (
                        <span
                          key={i}
                          className="px-3 py-1 bg-blue-50 text-blue-700 rounded-md text-sm font-medium border border-blue-200"
                        >
                          <Table2 className="w-3 h-3 inline mr-1" />
                          {sheet}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Columns Table */}
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700">Columns</label>
                      <button
                        onClick={handleAddColumn}
                        className="text-sm text-blue-600 hover:text-blue-700 flex items-center"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Add Column
                      </button>
                    </div>
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="text-left px-3 py-2 font-medium text-gray-600">Column Name</th>
                            <th className="text-left px-3 py-2 font-medium text-gray-600">Type</th>
                            <th className="text-left px-3 py-2 font-medium text-gray-600">Sample</th>
                            <th className="text-left px-3 py-2 font-medium text-gray-600 hidden md:table-cell">Description</th>
                            <th className="px-3 py-2 w-10"></th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {editableColumns.map((col, i) => (
                            <tr key={i} className="hover:bg-gray-50">
                              <td className="px-3 py-2">
                                <input
                                  type="text"
                                  value={col.name}
                                  onChange={(e) => handleColumnChange(i, 'name', e.target.value)}
                                  className="w-full px-2 py-1 border border-gray-200 rounded text-sm"
                                />
                              </td>
                              <td className="px-3 py-2">
                                <select
                                  value={col.type}
                                  onChange={(e) => handleColumnChange(i, 'type', e.target.value)}
                                  className="w-full px-2 py-1 border border-gray-200 rounded text-sm"
                                >
                                  {['Text', 'Number', 'Date', 'Formula', 'Dropdown', 'Checkbox', 'URL', 'Email'].map((t) => (
                                    <option key={t} value={t}>{t}</option>
                                  ))}
                                </select>
                              </td>
                              <td className="px-3 py-2">
                                <input
                                  type="text"
                                  value={col.sample}
                                  onChange={(e) => handleColumnChange(i, 'sample', e.target.value)}
                                  className="w-full px-2 py-1 border border-gray-200 rounded text-sm text-gray-500"
                                />
                              </td>
                              <td className="px-3 py-2 hidden md:table-cell">
                                <input
                                  type="text"
                                  value={col.description}
                                  onChange={(e) => handleColumnChange(i, 'description', e.target.value)}
                                  className="w-full px-2 py-1 border border-gray-200 rounded text-sm text-gray-500"
                                />
                              </td>
                              <td className="px-3 py-2">
                                <button
                                  onClick={() => handleRemoveColumn(i)}
                                  className="text-red-400 hover:text-red-600"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                {/* Publishing Options */}
                <div className="bg-white p-6 rounded-xl shadow border">
                  <h3 className="text-lg font-semibold mb-4">Publishing Options</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Pricing</label>
                      <div className="flex space-x-4">
                        <label className={`flex-1 p-4 rounded-lg border-2 cursor-pointer text-center ${
                          pricing === 'free' ? 'border-green-500 bg-green-50' : 'border-gray-200'
                        }`}>
                          <input
                            type="radio"
                            name="pricing"
                            value="free"
                            checked={pricing === 'free'}
                            onChange={() => setPricing('free')}
                            className="sr-only"
                          />
                          <span className="text-lg font-bold text-green-600">Free</span>
                          <p className="text-xs text-gray-500 mt-1">Available to everyone</p>
                        </label>
                        <label className={`flex-1 p-4 rounded-lg border-2 cursor-pointer text-center ${
                          pricing === 'premium' ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                        }`}>
                          <input
                            type="radio"
                            name="pricing"
                            value="premium"
                            checked={pricing === 'premium'}
                            onChange={() => setPricing('premium')}
                            className="sr-only"
                          />
                          <span className="text-lg font-bold text-blue-600">Premium</span>
                          <p className="text-xs text-gray-500 mt-1">Set your price</p>
                        </label>
                      </div>
                      {pricing === 'premium' && (
                        <div className="mt-3">
                          <label className="block text-sm font-medium text-gray-700 mb-1">Price (USD)</label>
                          <div className="relative">
                            <span className="absolute left-3 top-2 text-gray-500">$</span>
                            <input
                              type="number"
                              value={price}
                              onChange={(e) => setPrice(e.target.value)}
                              placeholder="9.99"
                              step="0.01"
                              min="0.99"
                              className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="flex space-x-3 pt-2">
                      <button
                        onClick={() => handleSave(false)}
                        disabled={saving}
                        className="flex-1 py-3 px-4 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 flex items-center justify-center space-x-2 disabled:opacity-50"
                      >
                        <Save className="w-4 h-4" />
                        <span>{saving ? 'Saving...' : 'Save as Draft'}</span>
                      </button>
                      <button
                        onClick={() => handleSave(true)}
                        disabled={saving}
                        className="flex-1 py-3 px-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-medium rounded-lg hover:from-blue-700 hover:to-purple-700 flex items-center justify-center space-x-2 disabled:opacity-50"
                      >
                        <Sparkles className="w-4 h-4" />
                        <span>{saving ? 'Publishing...' : 'Publish to Marketplace'}</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 4: Success */}
            {step === 4 && (
              <div className="bg-white p-12 rounded-xl shadow border text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Check className="w-8 h-8 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Template Created!</h2>
                <p className="text-gray-500 mb-8">
                  Your template "{templateName}" has been saved successfully.
                </p>
                <div className="flex justify-center space-x-4">
                  <button
                    onClick={() => setCurrentPage('templates')}
                    className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
                  >
                    <Eye className="w-4 h-4" />
                    <span>View in Template Manager</span>
                  </button>
                  <button
                    onClick={() => {
                      setStep(1);
                      setPrompt('');
                      setGeneratedTemplate(null);
                      setEditableColumns([]);
                      setTemplateName('');
                      setTemplateDescription('');
                      setPricing('free');
                      setPrice('');
                    }}
                    className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Build Another</span>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar - Build History */}
          <div className="lg:col-span-1">
            <div className="bg-white p-6 rounded-xl shadow border sticky top-24">
              <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center">
                <Clock className="w-4 h-4 mr-2" />
                Build History
              </h3>
              <div className="space-y-3">
                {MOCK_BUILD_HISTORY.map((build) => {
                  const platform = PLATFORMS.find((p) => p.id === build.platform);
                  return (
                    <button
                      key={build.id}
                      onClick={() => {
                        setPrompt(build.prompt);
                        setSelectedPlatform(build.platform);
                        setStep(1);
                      }}
                      className="w-full text-left p-3 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all"
                    >
                      <div className="flex items-start justify-between">
                        <p className="text-sm text-gray-700 line-clamp-2 flex-1">
                          {build.prompt}
                        </p>
                        <span className="text-lg ml-2 flex-shrink-0">{platform?.icon}</span>
                      </div>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-xs text-gray-400">{build.date}</span>
                        <span className="text-xs text-green-600 font-medium flex items-center">
                          <Check className="w-3 h-3 mr-1" />
                          {build.status}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Quick Stats */}
              <div className="mt-6 pt-6 border-t">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Quick Stats</h4>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Templates Created</span>
                    <span className="font-medium">12</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Published</span>
                    <span className="font-medium">8</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Total Downloads</span>
                    <span className="font-medium">1,240</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AITemplateBuilder;
